// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// WebAuthn step-up for at-rest key protection (Phase 2). Builds on the keystore: instead of sealing
// device keys with the always-available non-extractable browser key, the user can bind sealing to a
// SECURITY KEY or PLATFORM BIOMETRIC. A FIDO2 authenticator that supports the PRF extension returns
// a deterministic per-credential secret only after a user-verifying gesture (YubiKey tap / Touch ID
// / Windows Hello); we HKDF that into an AES-GCM key and use it to seal the keyring. So decrypting
// the local keys requires the physical authenticator — even a rooted/cloned device can't, because
// the secret never leaves the authenticator and is never stored.
//
// Per-user: the credential id + PRF salt live under `zalmoxis.webauthn.<userId>`. The derived key is
// non-extractable and held only in memory for the session.

interface WebauthnMeta {
  credentialId: string; // base64url
  prfSalt: string; // base64 — the PRF eval input
}

const RP_NAME = "Zalmoxis";

function metaKey(userId: string): string {
  return `zalmoxis.webauthn.${userId}`;
}

export function getWebauthnMeta(userId: string): WebauthnMeta | null {
  try {
    const raw = localStorage.getItem(metaKey(userId));
    return raw ? (JSON.parse(raw) as WebauthnMeta) : null;
  } catch {
    return null;
  }
}

// isWebauthnEnabled reports whether the user has bound key sealing to an authenticator.
export function isWebauthnEnabled(userId: string): boolean {
  return getWebauthnMeta(userId) !== null;
}

// webauthnSupported reports whether this browser can do WebAuthn at all (PRF is checked at register
// time, since support is per-authenticator).
export function webauthnSupported(): boolean {
  return typeof PublicKeyCredential !== "undefined" && typeof navigator !== "undefined" && !!navigator.credentials;
}

function b64(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function unb64(s: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
}
function rand(n: number): Uint8Array<ArrayBuffer> {
  const a = new Uint8Array(n);
  crypto.getRandomValues(a);
  return a;
}

// hkdfKey turns a raw PRF secret into a non-extractable AES-GCM key.
async function hkdfKey(prf: ArrayBuffer): Promise<CryptoKey> {
  const ikm = await crypto.subtle.importKey("raw", prf, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0), info: new TextEncoder().encode("zalmoxis-keystore-wrap") },
    ikm,
    { name: "AES-GCM", length: 256 },
    /* extractable */ false,
    ["encrypt", "decrypt"],
  );
}

function prfResult(cred: PublicKeyCredential): ArrayBuffer | null {
  const ext = cred.getClientExtensionResults() as { prf?: { results?: { first?: ArrayBuffer }; enabled?: boolean } };
  return ext.prf?.results?.first ?? null;
}

const NO_PRF =
  "This authenticator didn't provide a PRF secret — Zalmoxis needs PRF to derive the key. " +
  "Use a hardware security key (YubiKey) or a platform passkey (Touch ID / Windows Hello / Android). " +
  "Some software passkey managers (e.g. Bitwarden) don't support PRF yet.";

// registerWebauthn creates a resident credential bound to the user and returns the PRF-derived
// sealing key. PRF may be evaluated on create() (one tap) or need a follow-up get(); we try both.
// Throws with NO_PRF (and stores nothing) if the authenticator can't produce a PRF secret.
export async function registerWebauthn(userId: string, username: string): Promise<CryptoKey> {
  const userHandle = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(userId)));
  const prfSalt = rand(32);
  const cred = (await navigator.credentials.create({
    publicKey: {
      rp: { name: RP_NAME, id: location.hostname },
      user: { id: userHandle, name: username, displayName: username },
      challenge: rand(32),
      pubKeyCredParams: [
        { type: "public-key", alg: -7 }, // ES256
        { type: "public-key", alg: -257 }, // RS256
      ],
      authenticatorSelection: { userVerification: "preferred", residentKey: "preferred" },
      timeout: 60_000,
      extensions: { prf: { eval: { first: prfSalt } } },
    },
  })) as PublicKeyCredential | null;
  if (!cred) {
    throw new Error("registration cancelled");
  }
  const meta: WebauthnMeta = { credentialId: b64(cred.rawId), prfSalt: b64(prfSalt.buffer) };
  const prf = prfResult(cred) ?? (await assertPrf(meta)); // create-time PRF, else a follow-up tap
  if (!prf) {
    throw new Error(NO_PRF);
  }
  localStorage.setItem(metaKey(userId), JSON.stringify(meta)); // only persist a credential that actually yields PRF
  return hkdfKey(prf);
}

// assertPrf performs a PRF assertion (user gesture) and returns the raw secret, or null if the
// authenticator produced none.
async function assertPrf(meta: WebauthnMeta): Promise<ArrayBuffer | null> {
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: rand(32),
      allowCredentials: [{ type: "public-key", id: unb64(meta.credentialId) }],
      userVerification: "preferred",
      timeout: 60_000,
      extensions: { prf: { eval: { first: unb64(meta.prfSalt) } } },
    },
  })) as PublicKeyCredential | null;
  if (!assertion) {
    throw new Error("authentication cancelled");
  }
  return prfResult(assertion);
}

// unlockWebauthn re-derives the sealing key for an enabled user (prompts a user gesture).
export async function unlockWebauthn(userId: string): Promise<CryptoKey> {
  const meta = getWebauthnMeta(userId);
  if (!meta) {
    throw new Error("no security key registered");
  }
  const prf = await assertPrf(meta);
  if (!prf) {
    throw new Error(NO_PRF);
  }
  return hkdfKey(prf);
}

// forgetWebauthn removes the binding (after the keys have been re-sealed under the browser key).
export function forgetWebauthn(userId: string): void {
  localStorage.removeItem(metaKey(userId));
}
